import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'

interface NavigationThemeContextValue {
	isDarkBackground: boolean
	backgroundColor: string
	isScrolled: boolean
	setNavigationTheme: (isDark: boolean, bgColor?: string) => void
}

const NavigationThemeContext = createContext<NavigationThemeContextValue | undefined>(undefined)

export function NavigationThemeProvider({ children }: { children: ReactNode }) {
	// Default to dark background mode (white text) for better visibility on hero sections
	const [isDarkBackground, setIsDarkBackground] = useState(true)
	const [backgroundColor, setBackgroundColor] = useState('transparent')
	const [isScrolled, setIsScrolled] = useState(false)

	const setNavigationTheme = (isDark: boolean, bgColor?: string) => {
		setIsDarkBackground(isDark)
		setBackgroundColor(bgColor || 'transparent')

		// Also update body class for CSS fallbacks
		if (typeof document !== 'undefined') {
			document.body.classList.toggle('nav-on-dark', isDark)
		}
	}

	// Handle scroll detection
	useEffect(() => {
		const handleScroll = () => {
			const scrollY = window.scrollY
			setIsScrolled(scrollY > 10)
		}

		if (typeof window !== 'undefined') {
			window.addEventListener('scroll', handleScroll)
			handleScroll() // Check initial scroll position

			return () => {
				window.removeEventListener('scroll', handleScroll)
			}
		}
	}, [])

	// Clean up on unmount
	useEffect(() => {
		return () => {
			if (typeof document !== 'undefined') {
				document.body.classList.remove('nav-on-dark')
			}
		}
	}, [])

	return (
		<NavigationThemeContext.Provider value={{ isDarkBackground, backgroundColor, isScrolled, setNavigationTheme }}>
			{children}
		</NavigationThemeContext.Provider>
	)
}

export function useNavigationTheme() {
	const context = useContext(NavigationThemeContext)
	if (!context) {
		// Return default values if context is not available
		// Default to dark background mode (white text) for better visibility on hero sections
		return {
			isDarkBackground: true,
			backgroundColor: 'transparent',
			isScrolled: false,
			setNavigationTheme: () => {}
		}
	}
	return context
}